import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import {
  Button,
  CategoryBadge,
  Spinner,
  StatusBadge,
  Input,
} from '../../components/design-system';
import { AdminPage } from './AdminPage';
import { COUNTRIES } from '../../lib/countries';
import { compressImage } from '../../lib/media';

// Only the latest model per tier — no superseded versions (e.g. Sonnet 5,
// Opus 5 are gone now that 5.5 exists for both). Haiku 4.5 stays: it's still
// the latest Haiku available, nothing has superseded it.
const CLAUDE_MODELS = [
  { id: 'claude-sonnet-5-5', label: 'Claude Sonnet 5.5' },
  { id: 'claude-opus-5-5', label: 'Claude Opus 5.5' },
  { id: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5' },
];

interface AISettings {
  ai_mode: string;
  anthropic_api_key: string;
  ai_model: string;
  ai_temperature: string;
  ai_max_tokens: string;
}

interface AIPersona {
  id: string;
  name: string;
  avatar_url: string | null;
  video_url: string | null;
  origin_country: string;
  backstory: string;
  personality: string;
  communication_style: string;
  local_knowledge: string;
  language_primary: string;
  languages_additional: string[];
  interests: string;
  avoid_words: string;
  example_messages: string;
  active: boolean;
  created_at: string;
  updated_at: string;
}

interface PersonaForm {
  name: string;
  avatar_url: string | null;
  video_url: string | null;
  origin_country: string;
  backstory: string;
  personality: string;
  communication_style: string;
  local_knowledge: string;
  language_primary: string;
  languages_additional: string[];
  interests: string;
  avoid_words: string;
  example_messages: string;
  active: boolean;
  countries: string[];
}

const EMPTY_FORM: PersonaForm = {
  name: '', avatar_url: null, video_url: null, origin_country: '', backstory: '', personality: '',
  communication_style: '', local_knowledge: '', language_primary: 'English',
  languages_additional: [], interests: '', avoid_words: '', example_messages: '',
  active: true, countries: [],
};

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

export function AdminAIPersonas() {
  const { user } = useAuth();
  const [personas, setPersonas] = useState<(AIPersona & { countries: string[] })[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<(PersonaForm & { id?: string }) | null>(null);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [showPrompt, setShowPrompt] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLInputElement>(null);
  const [videoUploading, setVideoUploading] = useState(false);

  const DEFAULT_SETTINGS: AISettings = {
    ai_mode: 'sandbox',
    anthropic_api_key: '',
    ai_model: 'claude-sonnet-5-5',
    ai_temperature: '0.85',
    ai_max_tokens: '1024',
  };

  // API Settings state
  const [settings, setSettings] = useState<AISettings>(DEFAULT_SETTINGS);
  const [, setSettingsLoading] = useState(true);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [keyVisible, setKeyVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const loadSettings = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/manage-ai-settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ action: 'get' }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (json.settings) {
        const map: Record<string, string> = {};
        for (const s of json.settings) map[s.key] = s.value;
        setSettings({
          ai_mode: map.ai_mode ?? 'sandbox',
          anthropic_api_key: map.anthropic_api_key ?? '',
          ai_model: map.ai_model ?? 'claude-sonnet-5-5',
          ai_temperature: map.ai_temperature ?? '0.85',
          ai_max_tokens: map.ai_max_tokens ?? '1024',
        });
      }
    } catch (err) {
      console.error('Failed to load AI settings', err);
      setSettingsError('Edge function not deployed yet — showing defaults. Settings will work after the next deploy.');
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  async function saveSettings(updated: AISettings): Promise<boolean> {
    setSettingsSaving(true);
    setSettingsError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setSettingsError('Your session expired — sign in again and retry.');
        return false;
      }
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/manage-ai-settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          action: 'set',
          entries: Object.entries(updated).map(([key, value]) => ({ key, value })),
        }),
      });
      if (!res.ok) {
        // Without this check, a 401/403/500 was previously swallowed and the
        // UI still showed "Saved ✓" with local state updated as if the write
        // succeeded — silently leaving the DB on the old value (e.g. model,
        // or worse, ai_mode never actually flipping to live).
        let message = `Save failed (HTTP ${res.status}).`;
        try {
          const body = await res.json();
          if (body?.error) message = body.error;
        } catch { /* non-JSON error body, keep the generic message */ }
        setSettingsError(message);
        return false;
      }
      setSettings(updated);
      setSettingsSaved(true);
      setTimeout(() => setSettingsSaved(false), 1500);
      return true;
    } catch (err) {
      console.error('Failed to save AI settings', err);
      setSettingsError(err instanceof Error ? err.message : 'Save failed — check your connection and retry.');
      return false;
    } finally {
      setSettingsSaving(false);
    }
  }

  async function load() {
    const { data: personaRows } = await supabase
      .from('ai_personas')
      .select('*')
      .order('created_at', { ascending: true });
    const { data: countryRows } = await supabase
      .from('ai_persona_countries')
      .select('*');

    const countryMap = new Map<string, string[]>();
    for (const row of (countryRows ?? []) as { persona_id: string; country: string }[]) {
      const arr = countryMap.get(row.persona_id) ?? [];
      arr.push(row.country);
      countryMap.set(row.persona_id, arr);
    }

    setPersonas(
      ((personaRows ?? []) as AIPersona[]).map((p) => ({
        ...p,
        countries: countryMap.get(p.id) ?? [],
      })),
    );
    setLoading(false);
  }

  useEffect(() => { void load(); void loadSettings(); }, [loadSettings]);

  function openEditor(persona?: AIPersona & { countries: string[] }) {
    if (persona) {
      setEditing({
        id: persona.id,
        name: persona.name,
        avatar_url: persona.avatar_url,
        video_url: persona.video_url,
        origin_country: persona.origin_country,
        backstory: persona.backstory,
        personality: persona.personality,
        communication_style: persona.communication_style,
        local_knowledge: persona.local_knowledge,
        language_primary: persona.language_primary,
        languages_additional: persona.languages_additional,
        interests: persona.interests,
        avoid_words: persona.avoid_words,
        example_messages: persona.example_messages,
        active: persona.active,
        countries: persona.countries,
      });
    } else {
      setEditing({ ...EMPTY_FORM });
    }
    setSaveState('idle');
  }

  async function handleSave() {
    if (!editing) return;
    setSaveState('saving');
    const payload = {
      name: editing.name,
      avatar_url: editing.avatar_url || null,
      video_url: editing.video_url || null,
      origin_country: editing.origin_country,
      backstory: editing.backstory,
      personality: editing.personality,
      communication_style: editing.communication_style,
      local_knowledge: editing.local_knowledge,
      language_primary: editing.language_primary,
      languages_additional: editing.languages_additional,
      interests: editing.interests,
      avoid_words: editing.avoid_words,
      example_messages: editing.example_messages,
      active: editing.active,
      updated_at: new Date().toISOString(),
    };

    let personaId = editing.id;
    if (personaId) {
      await supabase.from('ai_personas').update(payload).eq('id', personaId);
    } else {
      const { data } = await supabase.from('ai_personas').insert(payload).select('id').single();
      personaId = data?.id;
    }

    if (personaId) {
      await supabase.from('ai_persona_countries').delete().eq('persona_id', personaId);
      if (editing.countries.length > 0) {
        await supabase.from('ai_persona_countries').insert(
          editing.countries.map((country) => ({ persona_id: personaId, country })),
        );
      }
    }

    setSaveState('saved');
    setTimeout(() => { setSaveState('idle'); setEditing(null); }, 1200);
    await load();
  }

  async function handleDelete(id: string) {
    await supabase.from('ai_personas').delete().eq('id', id);
    setDeleting(null);
    await load();
  }

  async function handleAvatarUpload(file: File) {
    if (!editing) return;
    setUploading(true);
    const optimized = await compressImage(file);
    const path = `${user!.id}/ai-personas/${Date.now()}-${optimized.name}`;
    const { data, error } = await supabase.storage.from('place-photos').upload(path, optimized, { upsert: true });
    if (!error && data) {
      const { data: url } = supabase.storage.from('place-photos').getPublicUrl(data.path);
      setEditing({ ...editing, avatar_url: url.publicUrl });
    }
    setUploading(false);
  }

  async function handleVideoUpload(file: File) {
    if (!editing) return;
    setVideoUploading(true);
    const path = `${user!.id}/ai-personas/${Date.now()}-${file.name}`;
    const { data, error } = await supabase.storage.from('host-videos').upload(path, file, { upsert: true });
    if (!error && data) {
      const { data: url } = supabase.storage.from('host-videos').getPublicUrl(data.path);
      setEditing({ ...editing, video_url: url.publicUrl });
    }
    setVideoUploading(false);
  }

  function toggleCountry(country: string) {
    if (!editing) return;
    const countries = editing.countries.includes(country)
      ? editing.countries.filter((c) => c !== country)
      : [...editing.countries, country];
    setEditing({ ...editing, countries });
  }

  function generateSystemPrompt(form: PersonaForm): string {
    const lines: string[] = [];
    lines.push(`You are ${form.name}, a warm and perceptive travel companion from ${form.origin_country}. Your purpose is to understand a traveler's taste through unhurried, genuine conversation — then help match them with a local host who can curate their perfect trip.`);
    if (form.personality) { lines.push(''); lines.push(`## Personality\n${form.personality}`); }
    if (form.backstory) { lines.push(''); lines.push(`## Backstory\n${form.backstory}`); }
    if (form.communication_style) { lines.push(''); lines.push(`## Communication Style\n${form.communication_style}`); }
    if (form.local_knowledge) { lines.push(''); lines.push(`## Local Knowledge & Expertise\n${form.local_knowledge}`); }
    if (form.interests) { lines.push(''); lines.push(`## Personal Interests & Taste\n${form.interests}`); }
    lines.push('');
    lines.push('## Conversation Guidelines');
    lines.push('- Ask at most 1–2 questions per message. Let the conversation breathe.');
    lines.push('- Mirror the traveler\'s energy and tone. If they write in fragments, respond in kind. If they\'re poetic, match it.');
    lines.push('- Use episodic reconstruction: invite them to recall specific past moments rather than state abstract preferences.');
    lines.push('- Follow the emotional thread. If something lights them up, go deeper before moving on.');
    lines.push('- Be curious, not clinical. You\'re building a portrait, not filling out a form.');
    lines.push('- After 6–10 exchanges you\'ll have what you need. Conclude naturally, not abruptly.');
    if (form.language_primary !== 'English' || form.languages_additional.length > 0) {
      lines.push('');
      lines.push(`## Languages`);
      lines.push(`Primary: ${form.language_primary}`);
      if (form.languages_additional.length > 0) {
        lines.push(`Also speaks: ${form.languages_additional.join(', ')}`);
      }
    }
    if (form.avoid_words) {
      lines.push('');
      lines.push(`## Words to Avoid\nNever use: ${form.avoid_words}`);
    }
    lines.push('');
    lines.push('## Important things to learn naturally (weave these in, don\'t ask as a checklist):');
    lines.push('- How many days they\'re planning to travel');
    lines.push('- When their trip starts — get an actual date if you can');
    lines.push('- Their pace, what they\'re drawn to, what they avoid');
    lines.push('');
    lines.push('## Closing the conversation');
    lines.push('When you genuinely understand how this person travels — their pace, their textures, what moves them — bring the conversation to a warm close. On that final message ONLY, append this marker on its own line: <<<ALMA_COMPLETE>>>');
    lines.push('Never mention the marker, never explain it, and never use it before you are truly ready.');
    lines.push('');
    lines.push('## Error handling');
    lines.push('If a technical issue occurs on your end, say exactly: "Give me a second — something went quiet on my end. Want to try again?"');
    return lines.join('\n');
  }

  const inputClass = 'rounded-md border border-app-border bg-app-bg px-3 py-2.5 text-sm text-app-fg placeholder:text-app-muted focus:border-gold focus:outline-none';

  if (loading) return <AdminPage title="AI Personas"><div className="flex justify-center py-16"><Spinner /></div></AdminPage>;

  // ─── List view ─────────────────────────────────────────────────────
  if (!editing) {
    return (
      <AdminPage
        title="AI Personas"
        description={`${personas.length} persona${personas.length !== 1 ? 's' : ''}`}
        actions={<Button onClick={() => openEditor()}>+ New persona</Button>}
      >
        {/* API Configuration — compact row */}
        <div className="bg-app-card border border-app-border rounded-2xl p-4 flex items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`w-2 h-2 rounded-full shrink-0 ${settings.ai_mode === 'live' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
            <div className="min-w-0">
              <h4 className="text-sm font-semibold text-app-fg">API Configuration</h4>
              <p className="text-xs text-app-muted mt-0.5 truncate">
                {settings.ai_mode === 'live'
                  ? `Live AI · ${CLAUDE_MODELS.find((m) => m.id === settings.ai_model)?.label ?? settings.ai_model}`
                  : 'Sandbox — scripted responses, no API credits used'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${settings.ai_mode === 'live' ? 'text-emerald-500 border-emerald-500/30 bg-emerald-500/10' : 'text-amber-500 border-amber-500/30 bg-amber-500/10'}`}>
              {settings.ai_mode === 'live' ? 'Live AI' : 'Sandbox'}
            </span>
            <button onClick={() => setShowSettings(true)} className="text-xs text-violet/70 hover:text-violet transition-colors">
              Configure
            </button>
          </div>
        </div>

        {personas.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-sm text-app-muted">No AI personas yet. Create Alma or another persona to get started.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {personas.map((p) => (
              <div key={p.id} className="bg-app-card border border-app-border rounded-2xl p-5 flex items-start gap-4">
                {/* Avatar */}
                <div className="w-14 h-14 rounded-full bg-app-bg border border-app-border flex items-center justify-center shrink-0 overflow-hidden">
                  {p.avatar_url ? (
                    <img src={p.avatar_url} alt={p.name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-xl font-bold text-app-muted">{p.name[0]}</span>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-base font-semibold text-app-fg">{p.name}</h3>
                    <StatusBadge tone={p.active ? 'success' : 'neutral'} label={p.active ? 'Active' : 'Inactive'} />
                  </div>
                  <p className="text-sm text-app-muted mb-2">From {p.origin_country}</p>
                  {p.countries.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {p.countries.map((c) => (
                        <CategoryBadge key={c} label={c} />
                      ))}
                    </div>
                  )}
                  {p.personality && <p className="text-xs text-app-muted line-clamp-2">{p.personality}</p>}
                </div>

                {/* Actions */}
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => setShowPrompt(p.id)} className="text-xs text-violet/70 hover:text-violet transition-colors">Prompt</button>
                  <button onClick={() => openEditor(p)} className="text-xs text-app-muted hover:text-app-fg transition-colors">Edit</button>
                  <button onClick={() => setDeleting(p.id)} className="text-xs text-crimson/60 hover:text-crimson transition-colors">Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Delete confirmation */}
        {deleting && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center px-4" onClick={() => setDeleting(null)}>
            <div className="bg-app-card border border-app-border rounded-2xl p-6 max-w-sm w-full text-center" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-semibold text-app-fg mb-2">Delete persona?</h3>
              <p className="text-sm text-app-muted mb-6">This cannot be undone.</p>
              <div className="flex gap-3 justify-center">
                <button onClick={() => setDeleting(null)} className="text-sm text-app-muted hover:text-app-fg px-4 py-2">Cancel</button>
                <Button onClick={() => void handleDelete(deleting)} className="bg-crimson hover:bg-crimson/90">Delete</Button>
              </div>
            </div>
          </div>
        )}

        {/* System prompt preview */}
        {showPrompt && (() => {
          const p = personas.find((x) => x.id === showPrompt);
          if (!p) return null;
          const prompt = generateSystemPrompt({ ...p, countries: p.countries });
          return (
            <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center" onClick={() => setShowPrompt(null)}>
              <div className="bg-app-card md:border md:border-app-border rounded-t-3xl md:rounded-2xl w-full max-w-2xl max-h-dvh-85 overflow-y-auto pb-safe-or-4" onClick={(e) => e.stopPropagation()}>
                <div className="sticky top-0 bg-app-card z-10 px-5 pt-5 pb-3 border-b border-app-border flex items-center justify-between gap-3">
                  <h3 className="text-lg font-semibold text-app-fg">System Prompt — {p.name}</h3>
                  <button onClick={() => setShowPrompt(null)} className="w-8 h-8 rounded-full bg-app-bg border border-app-border text-app-muted flex items-center justify-center hover:text-app-fg shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                      <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                    </svg>
                  </button>
                </div>
                <div className="px-5 py-5">
                  <pre className="text-xs text-app-fg whitespace-pre-wrap font-mono leading-relaxed bg-app-bg border border-app-border rounded-xl p-4">{prompt}</pre>
                  <button
                    onClick={() => { void navigator.clipboard.writeText(prompt); }}
                    className="mt-3 text-xs text-violet/70 hover:text-violet transition-colors"
                  >
                    Copy to clipboard
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* API Settings modal */}
        {showSettings && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center" onClick={() => setShowSettings(false)}>
            <div className="bg-app-card md:border md:border-app-border rounded-t-3xl md:rounded-2xl w-full max-w-lg max-h-dvh-85 overflow-y-auto pb-safe-or-4" onClick={(e) => e.stopPropagation()}>
              <div className="sticky top-0 bg-app-card z-10 px-5 pt-5 pb-3 border-b border-app-border flex items-center justify-between gap-3">
                <h3 className="text-lg font-semibold text-app-fg">API Configuration</h3>
                <button onClick={() => setShowSettings(false)} className="w-8 h-8 rounded-full bg-app-bg border border-app-border text-app-muted flex items-center justify-center hover:text-app-fg shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                    <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                  </svg>
                </button>
              </div>
              <div className="px-5 py-5 flex flex-col gap-5">
                {settingsError && (
                  <div className="flex items-center gap-2 rounded-xl border border-amber-500/20 bg-amber-500/5 px-4 py-3 text-xs text-amber-600">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 shrink-0">
                      <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                    </svg>
                    {settingsError}
                  </div>
                )}

                {/* Mode toggle */}
                <div className="flex items-center justify-between">
                  <FieldLabel label="Mode" hint="Live AI calls the Anthropic API and consumes credits. Sandbox returns scripted responses for testing." />
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setSettings({ ...settings, ai_mode: settings.ai_mode === 'live' ? 'sandbox' : 'live' })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings.ai_mode === 'live' ? 'bg-emerald-500' : 'bg-app-border'}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings.ai_mode === 'live' ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                    <span className={`text-xs font-medium w-14 ${settings.ai_mode === 'live' ? 'text-emerald-500' : 'text-amber-500'}`}>
                      {settings.ai_mode === 'live' ? 'Live AI' : 'Sandbox'}
                    </span>
                  </div>
                </div>

                {/* API Key */}
                <div className="flex flex-col gap-1">
                  <FieldLabel label="Anthropic API Key" hint="Your secret key from console.anthropic.com. Stored encrypted — never exposed in the browser." />
                  <div className="relative">
                    <input
                      type={keyVisible ? 'text' : 'password'}
                      value={settings.anthropic_api_key}
                      onChange={(e) => setSettings({ ...settings, anthropic_api_key: e.target.value })}
                      placeholder="sk-ant-…"
                      className={`w-full ${inputClass} pr-10 font-mono text-xs`}
                    />
                    <button
                      onClick={() => setKeyVisible(!keyVisible)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-app-muted hover:text-app-fg transition-colors"
                      title={keyVisible ? 'Hide' : 'Reveal'}
                    >
                      {keyVisible ? (
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                          <path fillRule="evenodd" d="M3.28 2.22a.75.75 0 00-1.06 1.06l14.5 14.5a.75.75 0 101.06-1.06l-1.745-1.745a10.029 10.029 0 003.3-4.38 1.651 1.651 0 000-1.185A10.004 10.004 0 009.999 3a9.956 9.956 0 00-4.744 1.194L3.28 2.22zM7.752 6.69l1.092 1.092a2.5 2.5 0 013.374 3.373l1.092 1.092a4 4 0 00-5.558-5.558z" clipRule="evenodd" />
                          <path d="M10.748 13.93l2.523 2.523a9.987 9.987 0 01-3.27.547c-4.258 0-7.894-2.66-9.337-6.41a1.651 1.651 0 010-1.186A10.007 10.007 0 012.839 6.02L6.07 9.252a4 4 0 004.678 4.678z" />
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                          <path d="M10 12.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5z" />
                          <path fillRule="evenodd" d="M.664 10.59a1.651 1.651 0 010-1.186A10.004 10.004 0 0110 3c4.257 0 7.893 2.66 9.336 6.41.147.381.146.804 0 1.186A10.004 10.004 0 0110 17c-4.257 0-7.893-2.66-9.336-6.41zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                {/* Model */}
                <div className="flex flex-col gap-1">
                  <FieldLabel label="Model" hint="Which Claude model powers conversations. Sonnet balances speed and quality. Opus is most capable but slower and costlier." />
                  <select
                    value={settings.ai_model}
                    onChange={(e) => setSettings({ ...settings, ai_model: e.target.value })}
                    className={`w-full appearance-none ${inputClass} pr-9`}
                  >
                    {CLAUDE_MODELS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
                  </select>
                </div>

                {/* Temperature */}
                <div className="flex flex-col gap-1">
                  <FieldLabel label={`Temperature (${settings.ai_temperature})`} hint="Controls randomness. Lower = more predictable responses. Higher = more creative and varied. 0.85 is a good default for natural conversation." />
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={settings.ai_temperature}
                    onChange={(e) => setSettings({ ...settings, ai_temperature: e.target.value })}
                    className="w-full accent-violet"
                  />
                  <div className="flex justify-between text-[10px] text-app-muted">
                    <span>Precise</span>
                    <span>Creative</span>
                  </div>
                </div>

                {/* Max tokens */}
                <div className="flex flex-col gap-1">
                  <FieldLabel label="Max tokens" hint="Maximum length of each AI response. 1024 tokens is roughly 750 words — enough for conversational messages without rambling." />
                  <input
                    type="number"
                    min="256"
                    max="4096"
                    step="256"
                    value={settings.ai_max_tokens}
                    onChange={(e) => setSettings({ ...settings, ai_max_tokens: e.target.value })}
                    className={`${inputClass} w-full`}
                  />
                </div>

                {/* Save */}
                <div className="flex items-center justify-between pt-2 border-t border-app-border">
                  <span className="text-xs text-app-muted">{settingsSaved ? 'Saved ✓' : ''}</span>
                  <Button
                    onClick={() => {
                      void saveSettings(settings).then((ok) => {
                        // Only auto-close on success — on failure, leave the
                        // modal open with the error banner so it isn't missed.
                        if (ok) setTimeout(() => setShowSettings(false), 800);
                      });
                    }}
                    disabled={settingsSaving}
                  >
                    {settingsSaving ? 'Saving…' : 'Save'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </AdminPage>
    );
  }

  // ─── Editor view (host-profile-style sections) ─────────────────────
  return (
    <AdminPage
      title={editing.id ? `Edit ${editing.name || 'Persona'}` : 'New AI Persona'}
      actions={
        <div className="flex items-center gap-3">
          <button onClick={() => setEditing(null)} className="text-sm text-app-muted hover:text-app-fg">Cancel</button>
          <Button onClick={() => void handleSave()} disabled={saveState === 'saving' || !editing.name.trim() || !editing.origin_country}>
            {saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? 'Saved ✓' : editing.id ? 'Save changes' : 'Create persona'}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-6">
        {/* Section: Identity */}
        <div className="bg-app-card border border-app-border rounded-2xl p-5 flex flex-col gap-4">
          <div>
            <h4 className="text-sm font-semibold text-app-fg">Identity</h4>
            <p className="text-xs text-app-muted mt-0.5">Who is this persona? Their name and origin.</p>
          </div>

          <div className="flex items-start gap-5">
            {/* Avatar */}
            <div className="flex flex-col items-center gap-2">
              <input ref={fileRef} type="file" accept="image/*,.heic,.heif" className="hidden" onChange={(e) => { if (e.target.files?.[0]) void handleAvatarUpload(e.target.files[0]); }} />
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="w-20 h-20 rounded-full bg-app-bg border-2 border-dashed border-app-border flex items-center justify-center overflow-hidden hover:border-gold transition-colors"
              >
                {editing.avatar_url ? (
                  <img src={editing.avatar_url} alt="" className="w-full h-full object-cover" />
                ) : uploading ? (
                  <Spinner size="sm" />
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-6 h-6 text-app-muted">
                    <path d="M1 8a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 018.07 3h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0016.07 6H17a2 2 0 012 2v7a2 2 0 01-2 2H3a2 2 0 01-2-2V8zm13.5 3a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM10 14a3 3 0 100-6 3 3 0 000 6z" />
                  </svg>
                )}
              </button>
              {editing.avatar_url && (
                <button onClick={() => setEditing({ ...editing, avatar_url: null })} className="text-[10px] text-crimson/60 hover:text-crimson">Remove</button>
              )}
            </div>

            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input label="Name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Alma" />
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-app-fg-2">Origin country</label>
                <select value={editing.origin_country} onChange={(e) => setEditing({ ...editing, origin_country: e.target.value })}
                  className={`w-full appearance-none ${inputClass} pr-9`}>
                  <option value="">Select country…</option>
                  {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
              className="rounded border-app-border bg-app-bg text-accent" />
            <span className="text-app-fg-2">Active — available for matching</span>
          </label>

          {/* Video */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-app-fg-2">Intro video</label>
            <input ref={videoRef} type="file" accept="video/*" className="hidden" onChange={(e) => { if (e.target.files?.[0]) void handleVideoUpload(e.target.files[0]); }} />
            {editing.video_url ? (
              <div className="flex flex-col gap-2">
                <video src={editing.video_url} controls className="rounded-xl border border-app-border w-full max-w-xs h-auto" />
                <div className="flex gap-3">
                  <button onClick={() => videoRef.current?.click()} className="text-xs text-violet/70 hover:text-violet">Replace</button>
                  <button onClick={() => setEditing({ ...editing, video_url: null })} className="text-xs text-crimson/60 hover:text-crimson">Remove</button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => videoRef.current?.click()}
                disabled={videoUploading}
                className="w-full max-w-xs h-24 rounded-xl border-2 border-dashed border-app-border bg-app-bg flex flex-col items-center justify-center gap-1 hover:border-gold transition-colors"
              >
                {videoUploading ? <Spinner size="sm" /> : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-app-muted">
                      <path d="M3.25 4A2.25 2.25 0 001 6.25v7.5A2.25 2.25 0 003.25 16h7.5A2.25 2.25 0 0013 13.75v-7.5A2.25 2.25 0 0010.75 4h-7.5zM19 4.75a.75.75 0 00-1.28-.53l-3 3a.75.75 0 00-.22.53v4.5c0 .199.079.39.22.53l3 3a.75.75 0 001.28-.53V4.75z" />
                    </svg>
                    <span className="text-xs text-app-muted">Upload video</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Section: Countries served */}
        <div className="bg-app-card border border-app-border rounded-2xl p-5 flex flex-col gap-4">
          <div>
            <h4 className="text-sm font-semibold text-app-fg">Countries served</h4>
            <p className="text-xs text-app-muted mt-0.5">Which countries can this persona handle conversations for?</p>
          </div>
          <CountryPicker selected={editing.countries} onToggle={toggleCountry} />
        </div>

        {/* Section: Personality & Backstory */}
        <div className="bg-app-card border border-app-border rounded-2xl p-5 flex flex-col gap-4">
          <div>
            <h4 className="text-sm font-semibold text-app-fg">Personality & Backstory</h4>
            <p className="text-xs text-app-muted mt-0.5">Define who this persona is as a person. This shapes their conversational tone.</p>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-app-fg-2">Personality</label>
            <textarea value={editing.personality} onChange={(e) => setEditing({ ...editing, personality: e.target.value })}
              rows={4} placeholder="Warm, curious, slightly poetic. Loves hearing people's stories. Has a calm energy that puts travelers at ease…"
              className={`${inputClass} resize-none`} />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-app-fg-2">Backstory</label>
            <textarea value={editing.backstory} onChange={(e) => setEditing({ ...editing, backstory: e.target.value })}
              rows={4} placeholder="Born in Lisbon, grew up between Alfama and Belém. Studied literature, worked in a small bookshop before becoming a travel guide…"
              className={`${inputClass} resize-none`} />
          </div>
        </div>

        {/* Section: Communication */}
        <div className="bg-app-card border border-app-border rounded-2xl p-5 flex flex-col gap-4">
          <div>
            <h4 className="text-sm font-semibold text-app-fg">Communication Style</h4>
            <p className="text-xs text-app-muted mt-0.5">How does this persona speak? How do they adapt to different travelers?</p>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-app-fg-2">Communication style</label>
            <textarea value={editing.communication_style} onChange={(e) => setEditing({ ...editing, communication_style: e.target.value })}
              rows={4} placeholder="Unhurried, uses evocative language. Mirrors the traveler's energy — if they're brief, keeps it concise. If they're detailed, leans in deeper…"
              className={`${inputClass} resize-none`} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label="Primary language" value={editing.language_primary} onChange={(e) => setEditing({ ...editing, language_primary: e.target.value })} placeholder="Portuguese" />
            <Input
              label="Additional languages"
              value={editing.languages_additional.join(', ')}
              onChange={(e) => setEditing({ ...editing, languages_additional: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
              placeholder="English, Spanish, French"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-app-fg-2">Words to avoid</label>
            <textarea value={editing.avoid_words} onChange={(e) => setEditing({ ...editing, avoid_words: e.target.value })}
              rows={2} placeholder="perfect, absolutely, certainly, amazing, wonderful, fantastic, incredible, awesome"
              className={`${inputClass} resize-none`} />
          </div>
        </div>

        {/* Section: Knowledge & Interests */}
        <div className="bg-app-card border border-app-border rounded-2xl p-5 flex flex-col gap-4">
          <div>
            <h4 className="text-sm font-semibold text-app-fg">Knowledge & Interests</h4>
            <p className="text-xs text-app-muted mt-0.5">What does this persona know deeply? What are their personal tastes?</p>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-app-fg-2">Local knowledge & expertise</label>
            <textarea value={editing.local_knowledge} onChange={(e) => setEditing({ ...editing, local_knowledge: e.target.value })}
              rows={4} placeholder="Knows every back-alley tascas in Lisbon, the best pastéis de nata outside Belém, hidden azulejo panels…"
              className={`${inputClass} resize-none`} />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-app-fg-2">Personal interests & taste</label>
            <textarea value={editing.interests} onChange={(e) => setEditing({ ...editing, interests: e.target.value })}
              rows={3} placeholder="Fado music, natural wine, old bookshops, tile art, slow mornings at riverside cafés…"
              className={`${inputClass} resize-none`} />
          </div>
        </div>

        {/* Section: Example messages */}
        <div className="bg-app-card border border-app-border rounded-2xl p-5 flex flex-col gap-4">
          <div>
            <h4 className="text-sm font-semibold text-app-fg">Example Messages</h4>
            <p className="text-xs text-app-muted mt-0.5">Write 2–3 example messages this persona would send, so admins can preview the tone.</p>
          </div>
          <div className="flex flex-col gap-1">
            <textarea value={editing.example_messages} onChange={(e) => setEditing({ ...editing, example_messages: e.target.value })}
              rows={6} placeholder={"\"I love hearing that — a place where you can hear the city breathe. That tells me a lot about you already.\"\n\n\"Tell me about a trip that felt exactly right. What do you remember most?\""}
              className={`${inputClass} resize-none`} />
          </div>
        </div>

        {/* Section: Generated system prompt preview */}
        <div className="bg-app-card border border-app-border rounded-2xl p-5 flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-semibold text-app-fg">Generated System Prompt</h4>
              <p className="text-xs text-app-muted mt-0.5">Auto-generated from the fields above. This is what the AI model receives.</p>
            </div>
            <button
              onClick={() => { void navigator.clipboard.writeText(generateSystemPrompt(editing)); }}
              className="text-xs text-violet/70 hover:text-violet transition-colors shrink-0"
            >
              Copy
            </button>
          </div>
          <pre className="text-xs text-app-fg whitespace-pre-wrap font-mono leading-relaxed bg-app-bg border border-app-border rounded-xl p-4 max-h-80 overflow-y-auto">
            {generateSystemPrompt(editing)}
          </pre>
        </div>
      </div>
    </AdminPage>
  );
}

function CountryPicker({ selected, onToggle }: { selected: string[]; onToggle: (c: string) => void }) {
  const [search, setSearch] = useState('');
  const filtered = search
    ? COUNTRIES.filter((c) => c.toLowerCase().includes(search.toLowerCase()))
    : COUNTRIES;

  return (
    <div className="flex flex-col gap-3">
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((c) => (
            <button
              key={c}
              onClick={() => onToggle(c)}
              className="text-xs bg-violet/10 text-violet border border-violet/20 rounded-full px-2.5 py-1 hover:bg-violet/20 transition-colors"
            >
              {c} ×
            </button>
          ))}
        </div>
      )}
      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search countries…"
        className="w-full rounded-full border border-app-border bg-app-bg pl-3 pr-3 py-2 text-sm text-app-fg placeholder:text-app-muted focus:border-gold focus:outline-none"
      />
      <div className="max-h-40 overflow-y-auto rounded-xl border border-app-border bg-app-bg">
        {filtered.map((c) => (
          <button
            key={c}
            onClick={() => onToggle(c)}
            className={`w-full text-left px-3 py-1.5 text-sm hover:bg-app-fg/5 transition-colors ${selected.includes(c) ? 'text-violet font-medium' : 'text-app-fg'}`}
          >
            {selected.includes(c) && '✓ '}{c}
          </button>
        ))}
      </div>
    </div>
  );
}

function FieldLabel({ label, hint }: { label: string; hint: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex items-center gap-1.5">
      <label className="text-sm font-medium text-app-fg-2">{label}</label>
      <div className="relative">
        <button
          onClick={() => setOpen(!open)}
          className="w-4 h-4 rounded-full bg-app-bg border border-app-border text-app-muted hover:text-app-fg flex items-center justify-center text-[10px] font-bold leading-none transition-colors"
        >
          ?
        </button>
        {open && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
            {/* Anchored left, not centered — these fields sit near the
                modal's left edge, and a centered w-56 box overflows past it. */}
            <div className="absolute left-0 bottom-full mb-2 z-50 w-56 rounded-xl bg-app-card border border-app-border shadow-lg p-3 text-xs text-app-muted leading-relaxed">
              {hint}
              <div className="absolute left-2 top-full w-2 h-2 bg-app-card border-r border-b border-app-border rotate-45 -mt-1" />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default AdminAIPersonas;
